import React, { useState } from 'react';

import { Badge, cn } from '@sandtech-hub-monorepo/ui-kit';
import { SkillsSectionEditDialog } from '../Edit/SkillsSectionEditDialog';
import ProfileEmptySection from './ProfileEmptySection';
import type { EmptyStateStyling } from './ProfileEmptySection';
import { ReactComponent as EmptySkillsIcon } from '../../assets/emptyState/emptySkills.svg';
import { ProfileCard } from './ProfileCard';

interface EmptyStateContent {
  header?: string;
  main?: string;
  callToActionText?: string;
  icon?: JSX.Element;
}

interface Props {
  editable?: boolean;
  skills: string[];
  /** Overrides the default outlined badge look; FIT passes a filled light-grey pill. */
  badgeClassName?: string;
  /** Empty-state copy; defaults to central-hub's original. FIT passes its own (Figma 10708-66006). */
  emptyState?: EmptyStateContent;
  /** Empty-state visual overrides; omitted by central-hub. */
  emptyStateStyling?: EmptyStateStyling;
}

export const SkillsSection = ({
  editable,
  skills,
  badgeClassName,
  emptyState,
  emptyStateStyling,
}: Props) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  return (
    <>
      <ProfileCard
        title="Skills"
        action={() => setEditDialogOpen(true)}
        editable
      >
        {skills.length ? (
          <div className="flex gap-2 flex-wrap">
            {skills.map((skill) => (
              <Badge
                key={skill}
                className={cn(
                  'text-sm border border-border bg-card hover:bg-muted text-foreground cursor-default',
                  badgeClassName
                )}
              >
                {skill}
              </Badge>
            ))}
          </div>
        ) : (
          <ProfileEmptySection
            header={emptyState?.header ?? 'Share your amazing skills!'}
            main={
              emptyState?.main ??
              `Tell us what you're good at to help employers find the right match`
            }
            callToActionText={emptyState?.callToActionText ?? 'Add Skills'}
            callToAction={() => setEditDialogOpen(true)}
            icon={emptyState?.icon ?? <EmptySkillsIcon />}
            {...emptyStateStyling}
          />
        )}
      </ProfileCard>

      {editable && (
        <SkillsSectionEditDialog
          defaultValues={skills}
          open={editDialogOpen}
          handleOpenChange={(open) => setEditDialogOpen(open)}
        />
      )}
    </>
  );
};
